/**
 * RescheduleInspectionModal Component
 *
 * Modern modal for rescheduling existing FleetHub inspection orders.
 * Uses the same visual calendar and optimal time selection as BookInspectionModal.
 * Shows only 2 steps (date → time) since the Prüfstelle is already in the order context.
 *
 * @module components/RescheduleInspectionModal
 */

import { FC, useState, useEffect, useMemo, useCallback } from "react"
import {
  Modal,
  View,
  ViewStyle,
  TextStyle,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Pressable,
} from "react-native"
import dayjs from "dayjs"
import DatePicker from "react-native-ui-datepicker"
import Icon from "react-native-vector-icons/MaterialIcons"

import "dayjs/locale/de"
import { Button } from "@/components/Button"
import { CustomTimePicker } from "@/components/CustomTimePicker"
import { Text } from "@/components/Text"
import {
  getLocationOpeningHours,
  getOptimalBookingTimesActive,
  type OpeningHoursResponse,
  type OptimalBookingTimesResponse,
  type WeeklySchedule,
} from "@/services/firebase/functions"
import { useFleetHubStore } from "@/stores/useFleetHubStore"
import type { FleetOrder } from "@/stores/useFleetHubStore"
import { colors, spacing } from "@/theme"

// Configure dayjs to use German locale
dayjs.locale("de")

export interface RescheduleInspectionModalProps {
  /**
   * Whether the modal is visible
   */
  visible: boolean

  /**
   * Callback when modal is closed
   */
  onClose: () => void

  /**
   * Order to reschedule (pre-populates form and provides Prüfstelle context)
   */
  order: FleetOrder | null
}

/**
 * Get default date (today) in YYYY-MM-DD format
 */
const getDefaultDate = (): string => {
  let date = dayjs()

  // If today is Sunday, default to tomorrow (Monday)
  if (date.day() === 0) {
    date = date.add(1, "day")
  }

  return date.format("YYYY-MM-DD")
}

/**
 * Format ISO date string to YYYY-MM-DD for input
 */
function formatDateForInput(dateString?: string): string {
  if (!dateString) return getDefaultDate()

  try {
    const date = new Date(dateString)
    return date.toISOString().split("T")[0]
  } catch {
    return getDefaultDate()
  }
}

/**
 * Hol- und Bring-Service is not offered on Saturdays. A driveby order may pick
 * one whenever the hub's schedule says it is open.
 */
function isHbsBlockedDate(serviceType: "pickup_delivery" | "driveby", date: dayjs.Dayjs): boolean {
  return serviceType === "pickup_delivery" && date.day() === 6
}

/**
 * Reschedule Inspection Modal
 *
 * Allows users to change inspection date/time for existing orders using modern UI.
 */
export const RescheduleInspectionModal: FC<RescheduleInspectionModalProps> = ({
  visible,
  onClose,
  order,
}) => {
  // Store hooks
  const rescheduleFleetOrder = useFleetHubStore((state) => state.rescheduleFleetOrder)
  const isReschedulingOrder = useFleetHubStore((state) => state.isReschedulingOrder)
  const rescheduleError = useFleetHubStore((state) => state.rescheduleError)

  // Step management (1 = date, 2 = time, 3 = confirmation)
  const [currentStep, setCurrentStep] = useState<number>(1)

  // Local state with defaults from order
  const [selectedDate, setSelectedDate] = useState<string>(getDefaultDate())
  const [selectedTime, setSelectedTime] = useState<string>("09:00")
  const [openingHours, setOpeningHours] = useState<OpeningHoursResponse | null>(null)
  const [loadingHours, setLoadingHours] = useState<boolean>(false)
  const [optimalTimes, setOptimalTimes] = useState<OptimalBookingTimesResponse | null>(null)
  const [loadingOptimalTimes, setLoadingOptimalTimes] = useState<boolean>(false)
  const [fetchTimeout, setFetchTimeout] = useState<boolean>(false)

  // Service type is a property of the order, not a choice made here (ADR-0051 §5):
  // `rescheduleFleetHubOrder` reads it from the order document and never from the
  // request, so a B2B customer has no self-service switch — the modal only shows it
  // and searches slots with it. A legacy order document without the field falls back
  // to HBS, which is what the modal assumed before.
  const serviceType: "pickup_delivery" | "driveby" =
    order?.serviceType === "driveby" ? "driveby" : "pickup_delivery"

  const serviceTypeLabel =
    serviceType === "pickup_delivery" ? "Hol- und Bring-Service" : "Selbstvorfahren"

  // Stable reference so CustomTimePicker's auto-advance effect doesn't re-run on
  // every parent render (see isDateBlocked usage below).
  const isDateBlocked = useCallback(
    (date: dayjs.Dayjs) => isHbsBlockedDate(serviceType, date),
    [serviceType],
  )

  // Extract Prüfstelle location from order
  const prufstelleLocation = useMemo(() => {
    if (!order) return null

    // Priority 1: Use order's assigned Prüfstelle
    if (order.zustandigePrufstelle) {
      // Map Prüfstelle names to standardized location names
      const locationMap: Record<string, string> = {
        "Nürnberg Süd": "Nürnberg",
        "München Nord": "München",
        // Add more mappings as needed
      }

      const mappedLocation = locationMap[order.zustandigePrufstelle]
      return mappedLocation || order.zustandigePrufstelle
    }

    // Priority 2: Use hub location name
    if (order.hubLocation?.name) {
      return order.hubLocation.name
    }

    // Fallback: Nürnberg
    return "Nürnberg"
  }, [order])

  // Reset form when modal opens
  useEffect(() => {
    if (visible && order) {
      setCurrentStep(1)
      setSelectedDate(formatDateForInput(order.scheduledPickupTime))
      setSelectedTime(order.spatesteAbholung || "09:00")
      setOpeningHours(null)
      setOptimalTimes(null)
      setLoadingHours(false)
      setLoadingOptimalTimes(false)
      setFetchTimeout(false)
    }
  }, [visible, order])

  // Fetch opening hours when modal opens
  useEffect(() => {
    const fetchOpeningHours = async () => {
      if (!visible || !prufstelleLocation) {
        return
      }

      setLoadingHours(true)
      try {
        console.log(
          "[RescheduleInspectionModal] Fetching opening hours for location:",
          prufstelleLocation,
        )
        const hours = await getLocationOpeningHours(prufstelleLocation)
        console.log("[RescheduleInspectionModal] Opening hours received:", {
          hasSchedule: !!hours.schedule,
          timezone: hours.timezone,
        })
        setOpeningHours(hours)
      } catch (error) {
        console.error("[RescheduleInspectionModal] Failed to fetch opening hours:", error)
        // Try fallback to Nürnberg
        try {
          const hours = await getLocationOpeningHours("Nürnberg")
          setOpeningHours(hours)
        } catch (fallbackError) {
          console.error(
            "[RescheduleInspectionModal] Failed to fetch fallback opening hours:",
            fallbackError,
          )
        }
      } finally {
        setLoadingHours(false)
      }
    }

    fetchOpeningHours()
  }, [visible, prufstelleLocation])

  // Fetch optimal times when date changes
  useEffect(() => {
    const fetchOptimalTimes = async () => {
      if (!visible || !selectedDate || !prufstelleLocation || !openingHours) {
        return
      }

      // An HBS order can never be rescheduled onto a Saturday, so there is nothing
      // to search for. The calendar already disables those days; this guards the
      // case where the order itself still carries one.
      if (isHbsBlockedDate(serviceType, dayjs(selectedDate))) {
        setOptimalTimes(null)
        setSelectedTime("")
        setLoadingOptimalTimes(false)
        return
      }

      setLoadingOptimalTimes(true)
      setFetchTimeout(false)

      // Set 7-second timeout
      const timeoutId = setTimeout(() => {
        console.warn("[RescheduleInspectionModal] Optimization timeout after 20s")
        setFetchTimeout(true)
        setLoadingOptimalTimes(false)
      }, 20000)

      try {
        const selectedDateObj = dayjs(selectedDate)
        const isToday = selectedDateObj.isSame(dayjs(), "day")

        // Determine current time
        let currentTime: string
        if (isToday) {
          currentTime = dayjs().format("HH:mm")
        } else {
          const dayNames = [
            "sunday",
            "monday",
            "tuesday",
            "wednesday",
            "thursday",
            "friday",
            "saturday",
          ]
          const dayName = dayNames[selectedDateObj.day()] as keyof WeeklySchedule
          const daySchedule = openingHours.schedule?.[dayName]
          currentTime = daySchedule?.open || "08:00"
        }

        // Extract resource counts
        const numEngineers = openingHours.resourceConfig?.numEngineers
        const numAssistants = openingHours.resourceConfig?.numAssistants

        console.log("[RescheduleInspectionModal] Fetching optimal times with params:", {
          location: prufstelleLocation,
          date: selectedDate,
          currentTime,
          serviceType,
          numEngineers,
          numAssistants,
        })

        const results = await getOptimalBookingTimesActive(
          prufstelleLocation,
          selectedDate,
          currentTime,
          serviceType,
        )

        clearTimeout(timeoutId)

        console.log("[RescheduleInspectionModal] Optimal times received:", {
          recommendedTime: results.recommendedTime,
          goodTimesCount: results.goodTimes.length,
        })

        setOptimalTimes(results)

        // Auto-select recommended time
        if (results.recommendedTime) {
          setSelectedTime(results.recommendedTime)
        }
      } catch (error) {
        clearTimeout(timeoutId)
        console.error("[RescheduleInspectionModal] Failed to fetch optimal times:", error)
        setOptimalTimes(null)
        setFetchTimeout(true)
      } finally {
        setLoadingOptimalTimes(false)
      }
    }

    // Only fetch optimal times when on step 2 (time selection)
    if (currentStep === 2) {
      fetchOptimalTimes()
    }
  }, [visible, selectedDate, prufstelleLocation, openingHours, currentStep, serviceType])

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    if (!order || !selectedDate || !selectedTime) {
      return
    }

    try {
      await rescheduleFleetOrder(order.id, {
        abholdatum: selectedDate,
        spatesteAbholung: selectedTime,
      })
    } catch (error) {
      console.error("[RescheduleInspectionModal] Error rescheduling order:", error)
    }
  }

  /**
   * Handle date selection and move to time picker
   */
  const handleDateSelected = () => {
    if (!selectedDate) {
      return
    }

    const dateObj = dayjs(selectedDate)

    // Check if date is a Sunday, or a Saturday on an HBS order
    if (dateObj.day() === 0 || isHbsBlockedDate(serviceType, dateObj)) {
      return
    }

    // Move to time picker
    setCurrentStep(2)
  }

  /**
   * Handle back navigation
   */
  const handleBackToDate = () => {
    if (currentStep === 3) {
      // Go back from confirmation to time selection
      setCurrentStep(2)
    } else if (currentStep === 2) {
      // Go back from time to date selection
      setCurrentStep(1)
    }
  }

  /**
   * Handle modal close
   */
  const handleClose = () => {
    onClose()
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
      <Pressable style={$modalOverlay} onPress={handleClose}>
        <Pressable style={$modalContainer} onPress={(e) => e.stopPropagation()}>
          {/* Header */}
          <View style={$header}>
            {(currentStep === 2 || currentStep === 3) && (
              <TouchableOpacity
                onPress={handleBackToDate}
                activeOpacity={0.7}
                style={{ marginRight: spacing.sm }}
              >
                <Icon name="arrow-back" size={24} color={colors.text} />
              </TouchableOpacity>
            )}
            <Text style={$headerTitle}>
              {currentStep === 1
                ? "Abholdatum wählen"
                : currentStep === 2
                  ? "Abholzeit wählen"
                  : "Buchung bestätigen"}
            </Text>
            <TouchableOpacity onPress={handleClose} style={$closeButton}>
              <Icon name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          {/* Service type — read-only, seeded from the order (ADR-0051 §5) */}
          <View style={$serviceTypeContainer}>
            <View style={$serviceTypeBadge}>
              <Icon
                name={serviceType === "pickup_delivery" ? "local-shipping" : "directions-car"}
                size={16}
                color={colors.palette.neutral600}
              />
              <Text testID="reschedule-service-type-label" style={$serviceTypeText}>
                {serviceTypeLabel}
              </Text>
            </View>
          </View>

          <ScrollView contentContainerStyle={$content} showsVerticalScrollIndicator={false}>
            {/* Order Info */}
            {order && (
              <View style={$orderInfo}>
                <Text style={$orderInfoLabel}>Auftrag</Text>
                <Text style={$orderInfoValue}>#{order.id}</Text>
                {order.numberPlate && <Text style={$orderInfoValue}>{order.numberPlate}</Text>}
                {prufstelleLocation && (
                  <Text style={$orderInfoValue}>Prüfstelle: {prufstelleLocation}</Text>
                )}
              </View>
            )}

            {/* Step 1: Date Selection */}
            {currentStep === 1 && (
              <>
                {loadingHours ? (
                  <View style={$loadingContainer}>
                    <ActivityIndicator size="large" color={colors.primary} />
                    <Text style={$loadingText}>Öffnungszeiten werden geladen...</Text>
                  </View>
                ) : (
                  <View style={$calendarWrapper}>
                    <DatePicker
                      mode="single"
                      date={selectedDate ? dayjs(selectedDate) : undefined}
                      minDate={dayjs()}
                      disabledDates={(date) => {
                        if (!date) return false
                        const dateStr = dayjs(date).format("YYYY-MM-DD")
                        const dayOfWeek = dayjs(date).day()
                        const isToday = dayjs(date).isSame(dayjs(), "day")

                        // Disable past dates
                        if (dayjs(date).isBefore(dayjs(), "day")) return true

                        // No Hol- und Bring-Service on Saturdays
                        if (isHbsBlockedDate(serviceType, dayjs(date))) return true

                        // Check if today and past closing time
                        if (isToday && openingHours?.schedule) {
                          const currentTime = dayjs().format("HH:mm")
                          const dayNames = [
                            "sunday",
                            "monday",
                            "tuesday",
                            "wednesday",
                            "thursday",
                            "friday",
                            "saturday",
                          ]
                          const dayName = dayNames[dayOfWeek] as keyof WeeklySchedule
                          const daySchedule = openingHours.schedule[dayName]

                          if (
                            !daySchedule?.isOpen ||
                            (daySchedule?.close && currentTime >= daySchedule.close)
                          ) {
                            return true
                          }
                        }

                        // Check holidays
                        if (openingHours?.specialDates?.[dateStr]?.isOpen === false) return true

                        // Check weekly schedule
                        const dayNames = [
                          "sunday",
                          "monday",
                          "tuesday",
                          "wednesday",
                          "thursday",
                          "friday",
                          "saturday",
                        ]
                        const dayName = dayNames[dayOfWeek] as keyof WeeklySchedule
                        return openingHours?.schedule
                          ? !openingHours.schedule[dayName]?.isOpen
                          : dayOfWeek === 0
                      }}
                      onChange={(params: any) => {
                        const newDate = dayjs(params.date).format("YYYY-MM-DD")
                        setSelectedDate(newDate)
                      }}
                      components={{
                        IconPrev: <Icon name="chevron-left" size={24} color={colors.text} />,
                        IconNext: <Icon name="chevron-right" size={24} color={colors.text} />,
                      }}
                      styles={{
                        selected: {
                          backgroundColor: colors.primary,
                        },
                        selected_label: {
                          color: colors.surface,
                          fontWeight: "600",
                        },
                        today: {
                          borderColor: colors.primary,
                          borderWidth: 2,
                        },
                        disabled: {
                          opacity: 0.3,
                        },
                        disabled_label: {
                          color: colors.textDim,
                          textDecorationLine: "line-through",
                        },
                      }}
                    />
                  </View>
                )}

                <View style={$buttonContainer}>
                  <Button text="Abbrechen" onPress={handleClose} preset="default" style={$button} />
                  <Button
                    text="Weiter"
                    onPress={handleDateSelected}
                    preset="filled"
                    style={[$button, $buttonPrimary]}
                    disabled={!selectedDate}
                  />
                </View>
              </>
            )}

            {/* Step 2: Time Selection */}
            {currentStep === 2 && (
              <CustomTimePicker
                visible={true}
                onApply={(time) => setSelectedTime(time)}
                onClose={(time) => {
                  setSelectedTime(time)
                  setCurrentStep(3)
                }}
                selectedDate={selectedDate}
                initialTime={selectedTime}
                openingHours={openingHours}
                optimalTimes={optimalTimes}
                loadingOptimalTimes={loadingOptimalTimes}
                fetchTimeout={fetchTimeout}
                onDateChange={setSelectedDate}
                isDateBlocked={isDateBlocked}
              />
            )}

            {/* Step 3: Confirmation */}
            {currentStep === 3 && (
              <>
                <Text style={$stepTitle}>Buchung bestätigen</Text>

                {/* Summary Card */}
                <View style={$summaryCard}>
                  <View style={$summaryRow}>
                    <Icon name="calendar-today" size={20} color={colors.textDim} />
                    <View style={$summaryContent}>
                      <Text style={$summaryLabel}>Neues Abholdatum</Text>
                      <Text style={$summaryValue}>
                        {dayjs(selectedDate).format("DD. MMMM YYYY")}
                      </Text>
                    </View>
                  </View>

                  <View style={$summaryRow}>
                    <Icon name="schedule" size={20} color={colors.textDim} />
                    <View style={$summaryContent}>
                      <Text style={$summaryLabel}>Neue Abholzeit</Text>
                      <Text style={$summaryValue}>{selectedTime} Uhr</Text>
                    </View>
                  </View>

                  {prufstelleLocation && (
                    <View style={$summaryRow}>
                      <Icon name="business" size={20} color={colors.textDim} />
                      <View style={$summaryContent}>
                        <Text style={$summaryLabel}>Prüfstelle</Text>
                        <Text style={$summaryValue}>{prufstelleLocation}</Text>
                      </View>
                    </View>
                  )}
                </View>

                {/* Error Message */}
                {rescheduleError && (
                  <View style={$errorContainer}>
                    <Icon name="error-outline" size={20} color={colors.error} />
                    <Text style={$errorText}>{rescheduleError}</Text>
                  </View>
                )}

                {/* Action Buttons */}
                <View style={$buttonContainer}>
                  <Button
                    text="Zurück"
                    onPress={() => setCurrentStep(2)}
                    preset="default"
                    style={$button}
                    disabled={isReschedulingOrder}
                  />
                  <Button
                    text={isReschedulingOrder ? "Wird verschoben..." : "Verschieben"}
                    onPress={handleSubmit}
                    preset="filled"
                    style={[$button, $buttonPrimary]}
                    disabled={isReschedulingOrder}
                    RightAccessory={
                      isReschedulingOrder
                        ? () => <ActivityIndicator size="small" color={colors.surface} />
                        : undefined
                    }
                  />
                </View>
              </>
            )}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  )
}

// Styles
const $modalOverlay: ViewStyle = {
  flex: 1,
  backgroundColor: colors.overlay || "rgba(17, 30, 44, 0.5)",
  justifyContent: "center",
  alignItems: "center",
  padding: spacing.xl,
}

const $modalContainer: ViewStyle = {
  backgroundColor: colors.surface,
  borderRadius: 12,
  width: "100%",
  maxWidth: 500,
  maxHeight: "90%",
  // @ts-ignore - Web-specific
  boxShadow: "0 8px 24px rgba(17, 30, 44, 0.25)",
  overflow: "hidden",
}

const $header: ViewStyle = {
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.lg,
  borderBottomWidth: 1,
  borderBottomColor: colors.border || colors.palette.neutral200,
}

const $headerTitle: TextStyle = {
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
  flex: 1,
}

const $closeButton: ViewStyle = {
  padding: spacing.xs,
  borderRadius: 8,
  cursor: "pointer" as any,
}

const $serviceTypeContainer: ViewStyle = {
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.xs,
  backgroundColor: colors.surface,
}

const $serviceTypeBadge: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  alignSelf: "flex-start",
  gap: spacing.xs,
  backgroundColor: colors.surfaceSecondary,
  borderRadius: 8,
  paddingVertical: 6,
  paddingHorizontal: 12,
}

const $serviceTypeText: TextStyle = {
  fontSize: 13,
  fontWeight: "500",
  color: colors.palette.neutral600,
}

const $content: ViewStyle = {
  paddingHorizontal: spacing.xl,
  paddingVertical: spacing.lg,
}

const $orderInfo: ViewStyle = {
  backgroundColor: colors.palette.neutral100,
  padding: spacing.md,
  borderRadius: 8,
  marginBottom: spacing.lg,
}

const $orderInfoLabel: TextStyle = {
  fontSize: 12,
  fontWeight: "600",
  color: colors.textDim,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  marginBottom: spacing.xxs,
}

const $orderInfoValue: TextStyle = {
  fontSize: 14,
  fontWeight: "600",
  color: colors.text,
  marginBottom: spacing.xxs,
}

const $calendarWrapper: ViewStyle = {
  width: "100%",
  marginBottom: spacing.lg,
}

const $buttonContainer: ViewStyle = {
  flexDirection: "row",
  gap: spacing.sm,
  marginTop: spacing.md,
}

const $button: ViewStyle = {
  flex: 1,
}

const $buttonPrimary: ViewStyle = {
  backgroundColor: colors.primary,
}

const $loadingContainer: ViewStyle = {
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.xl,
}

const $loadingText: TextStyle = {
  fontSize: 13,
  color: colors.textDim,
  fontWeight: "500",
  marginTop: spacing.sm,
}

const $errorContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  backgroundColor: colors.errorBgLight,
  padding: spacing.sm,
  borderRadius: 8,
  marginTop: spacing.md,
}

const $errorText: TextStyle = {
  fontSize: 14,
  color: colors.error,
  marginLeft: spacing.xs,
  flex: 1,
}

const $stepTitle: TextStyle = {
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
  marginBottom: spacing.md,
}

const $summaryCard: ViewStyle = {
  backgroundColor: colors.modalInputBg,
  borderRadius: 12,
  padding: spacing.md,
  marginBottom: spacing.lg,
  borderWidth: 1,
  borderColor: colors.modalBorder,
}

const $summaryRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "flex-start",
  marginBottom: spacing.md,
}

const $summaryContent: ViewStyle = {
  flex: 1,
  marginLeft: spacing.sm,
}

const $summaryLabel: TextStyle = {
  fontSize: 12,
  color: colors.textDim,
  marginBottom: spacing.xxs,
}

const $summaryValue: TextStyle = {
  fontSize: 16,
  fontWeight: "600",
  color: colors.text,
}
